/* @flow */
import _ from 'lodash';
import type {
  ReduxState,
  Action,
  Fetchable,
  MediaSearchParams,
  MediaSearchFullParams,
  MediaSearchResults,
  Aggregations,
  MediaAggregationKey,
} from '../../types';
import { mediaSearchParamsProps } from '../../types/MediaSearchParams';
import { getSearchParams, getSubmittedSearchParams } from '../../redux/reducers/app';

// ===========================
//   STATE TYPE
// ===========================
export type MediaSearchState = {
  searchParams: MediaSearchParams,
  submittedSearchParams: MediaSearchFullParams,
  sections: Fetchable<MediaSearchResults>,
  pageScroll: number,
  expandedCategories: {
    [categoryId: number]: boolean,
  },
  slideIndexByCategory: {
    [categoryId: number]: number,
  },
};

const initialPageScroll = 0;
const initialExpandedCategories = {};
const initialSlideIndexByCategory = {};

// ===========================
//   INITIAL STATE
// ===========================
export const initialState: MediaSearchState = {
  searchParams: { categoryIds: [] },
  submittedSearchParams: { categoryIds: [], text: '' },
  categories: { fetching: false },
  sections: { fetching: false },
  pageScroll: initialPageScroll,
  expandedCategories: initialExpandedCategories,
  slideIndexByCategory: initialSlideIndexByCategory,
};

const isStaleRequest = (params?: MediaSearchParams, requestParams: MediaSearchParams) => {
  return !_.isEqual(params, requestParams);
};

// ===========================
//   REDUCER
// ===========================
export default (state: MediaSearchState = initialState, action: Action): MediaSearchState => {
  switch (action.type) {
    case 'SET_MEDIA_SEARCH': {
      const fullSearchParams: MediaSearchFullParams = action.payload;
      const searchParams: MediaSearchParams = _.pick(fullSearchParams, mediaSearchParamsProps);
      return { ...state, searchParams };
    }
    case 'FETCH_CATEGORIES_SUCCESS': {
      // const data = action.payload;
      return {
        ...state,
        searchParams: {
          ...state.searchParams,
          // categoryIds: data.map(_ => _.id),
          categoryIds: [],
          pagination: {
            start: 0,
            end: 1000,
          },
        },
      };
    }
    case 'PERFORM_MEDIA_SEARCH_REQUEST': {
      const fullSearchParams: MediaSearchFullParams = action.payload;
      const searchParams: MediaSearchParams = _.pick(fullSearchParams, mediaSearchParamsProps);
      return {
        ...state,
        searchParams,
        submittedSearchParams: fullSearchParams,
        sections: { data: state.sections.data, fetching: true },
      };
    }
    case 'PERFORM_MEDIA_SEARCH_SUCCESS': {
      if (!action.meta.initialPayload.text) {
        return {
          ...state,
          sections: { fetching: false },
          pageScroll: initialPageScroll,
          expandedCategories: initialExpandedCategories,
          slideIndexByCategory: initialSlideIndexByCategory,
        };
      }
      if (!isStaleRequest(state.submittedSearchParams, action.meta.initialPayload)) {
        const data = action.payload;
        return {
          ...state,
          sections: { data, fetching: false },
          pageScroll: initialPageScroll,
          expandedCategories: initialExpandedCategories,
          slideIndexByCategory: initialSlideIndexByCategory,
        };
      } else {
        // ignore results
        return state;
      }
    }
    case 'PERFORM_MEDIA_SEARCH_FAILURE': {
      if (!isStaleRequest(state.submittedSearchParams, action.meta.initialPayload)) {
        const error = action.payload;
        return {
          ...state,
          sections: { error, fetching: false },
          pageScroll: initialPageScroll,
          expandedCategories: initialExpandedCategories,
          slideIndexByCategory: initialSlideIndexByCategory,
        };
      } else {
        // ignore results
        return state;
      }
    }
    case 'MEDIA_SEARCH_SCROLL_PAGE': {
      const pageScroll = action.payload || 0;
      return {
        ...state,
        pageScroll,
      };
    }
    case 'MEDIA_SEARCH_TOGGLE_EXPAND_CATEGORY': {
      const categoryId = action.payload;
      return {
        ...state,
        expandedCategories: {
          ...state.expandedCategories,
          [categoryId]: !state.expandedCategories[categoryId],
        },
      };
    }
    case 'MEDIA_SEARCH_SET_CATEGORY_SLIDE': {
      const { categoryId, slideIndex } = action.payload;
      return {
        ...state,
        slideIndexByCategory: {
          ...state.slideIndexByCategory,
          [categoryId]: slideIndex || 0,
        },
      };
    }
    case 'LOGOUT_SUCCESS':
      return initialState;

    default: {
      return state;
    }
  }
};

// ===========================
//   SELECTORS
// ===========================

export const getSelectedCategoryIds = (state: ReduxState): number[] => {
  return state.mediaSearch.searchParams.categoryIds;
};

export const getSections = (state: ReduxState): Fetchable<MediaSearchResults> => {
  return state.mediaSearch.sections;
};

export const getAggregations = (
  state: ReduxState
): Fetchable<Aggregations<MediaAggregationKey>> => {
  const { fetching, error, data } = state.mediaSearch.sections;
  return {
    fetching,
    error,
    data: data && data.aggregations,
  };
};

export const getMediaSearchParams = (state: ReduxState): MediaSearchFullParams => {
  return _.merge({}, getSearchParams(state), state.mediaSearch.searchParams);
};

export const getSubmittedMediaSearchParams = (state: ReduxState): MediaSearchFullParams => {
  return _.merge({}, getSubmittedSearchParams(state), state.mediaSearch.submittedSearchParams);
};
